import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { customersAPI, projectsAPI, tenantAPI } from "../../../api/client";
import useAuth from "../../../hooks/useAuth";
import { PERMISSIONS } from "../../../constants/permissions";
import "../../../components/projects/Projects.css";
import { formatProjectStatus } from "../../../utils/formatProjectStatus";
import { todayString } from "../../../utils/dateUtils";

function toggleMultiSelect(setter, value) {
  setter((current) =>
    current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value],
  );
}

function getFilterLabel(selectedValues, allOptions, fallback) {
  if (!selectedValues.length) return fallback;
  const labels = allOptions
    .filter((option) => selectedValues.includes(option))
    .map((option) => option);
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "Flagged today";
  if (days === 1) return "Flagged yesterday";
  return `Flagged ${days} days ago`;
}

const FILTER_STORAGE_KEY = "invoicingPageFilters";

function readStoredFilters() {
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStoredFilters(filters) {
  try {
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch {}
}

export default function InvoicingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const storedFilters = useMemo(() => readStoredFilters(), []);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [readyCount, setReadyCount] = useState(0);
  const [invoicedCount, setInvoicedCount] = useState(0);
  const [activeTab, setActiveTab] = useState(storedFilters.activeTab ?? "READY_FOR_INVOICING");
  const [marking, setMarking] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(storedFilters.search ?? "");
  const [filterCustomers, setFilterCustomers] = useState(storedFilters.filterCustomers ?? []);
  const [filterWorkflows, setFilterWorkflows] = useState(storedFilters.filterWorkflows ?? []);
  const sentinelRef = useRef(null);
  const scrollBodyRef = useRef(null);
  const [invoicingInputType, setInvoicingInputType] = useState("text");
  const [invoiceInputs, setInvoiceInputs] = useState({});
  const [allCustomerOptions, setAllCustomerOptions] = useState([]);
  const [allWorkflowOptions, setAllWorkflowOptions] = useState([]);
  const fileInputRefs = useRef({});
  const searchDebounceRef = useRef(null);
  const pageRef = useRef(1);
  const isFetchingRef = useRef(false);
  const currentParamsRef = useRef({});
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const canView =
    !!user?.isAdmin || !!user?.permissions?.includes(PERMISSIONS.INVOICING_VIEW);
  const canManage =
    !!user?.isAdmin ||
    !!user?.permissions?.includes(PERMISSIONS.INVOICING_MANAGE);

  useEffect(() => {
    if (!user) return;
    if (!canView) navigate("/");
  }, [user, canView, navigate]);

  const fetchQueue = useCallback((params, pageNum = 1) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    currentParamsRef.current = params;
    if (pageNum === 1) {
      setLoading(true);
      setItems([]);
      setTotalItems(0);
    } else {
      setIsFetchingMore(true);
    }
    setError("");
    projectsAPI.getInvoicingQueue({ ...params, page: pageNum, limit: 20 })
      .then((res) => {
        const { data = [], total = 0, readyCount: rc = 0, invoicedCount: ic = 0 } = res.data || {};
        pageRef.current = pageNum;
        setItems((prev) => pageNum === 1 ? data : [...prev, ...data]);
        setTotalItems(total);
        setReadyCount(rc);
        setInvoicedCount(ic);
      })
      .catch((err) => setError(err.response?.data?.message || "Failed to load invoicing queue"))
      .finally(() => {
        setLoading(false);
        setIsFetchingMore(false);
        isFetchingRef.current = false;
      });
  }, []);

  useEffect(() => {
    if (!canView) return;
    Promise.allSettled([tenantAPI.getTenant(), customersAPI.getAll(), projectsAPI.workflowOptions()])
      .then(([tenantResult, customersResult, workflowsResult]) => {
        const schema = tenantResult.status === "fulfilled"
          ? (tenantResult.value?.data?.projectFormSchema || {})
          : {};
        setInvoicingInputType(schema.invoicingInputType || "text");
        setAllCustomerOptions(
          customersResult.status === "fulfilled"
            ? (customersResult.value.data || []).map((c) => c.name).sort()
            : [],
        );
        setAllWorkflowOptions(
          workflowsResult.status === "fulfilled"
            ? (workflowsResult.value.data || []).map((w) => w.name).sort()
            : [],
        );
      });
  }, [canView]);

  useEffect(() => {
    saveStoredFilters({ search, filterCustomers, filterWorkflows, activeTab });
  }, [search, filterCustomers, filterWorkflows, activeTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      document.querySelectorAll("details.multi-filter-dropdown[open]").forEach((dropdown) => {
        if (!dropdown.contains(event.target)) {
          dropdown.removeAttribute("open");
        }
      });
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!canView) return;
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchQueue({
        status: activeTab,
        search: search || undefined,
        customers: filterCustomers.length ? filterCustomers.join(",") : undefined,
        workflows: filterWorkflows.length ? filterWorkflows.join(",") : undefined,
      });
    }, search ? 300 : 0);
    return () => clearTimeout(searchDebounceRef.current);
  }, [canView, activeTab, search, filterCustomers, filterWorkflows, fetchQueue]);

  const handleMarkInvoiced = async (id) => {
    const input = invoiceInputs[id];
    if (invoicingInputType === "text" && !String(input || "").trim()) {
      setError("Please enter an invoice number before marking as invoiced.");
      return;
    }
    if (invoicingInputType === "checkbox" && !input) {
      setError("Please confirm the invoice is done before marking as invoiced.");
      return;
    }
    if (invoicingInputType === "file" && !input) {
      setError("Please upload an invoice file before marking as invoiced.");
      return;
    }

    setMarking(id);
    setError("");
    try {
      const payload =
        invoicingInputType === "text"
          ? { invoiceNumber: String(input).trim() }
          : invoicingInputType === "file"
            ? { invoiceFile: input }
            : {};
      const res = await projectsAPI.markInvoiced(id, payload);
      setItems((prev) => prev.filter((item) => item.id !== id));
      setTotalItems((prev) => Math.max(0, prev - 1));
      setReadyCount((prev) => Math.max(0, prev - 1));
      setInvoicedCount((prev) => prev + 1);
      setInvoiceInputs((prev) => { const next = { ...prev }; delete next[id]; return next; });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to mark as invoiced",
      );
    } finally {
      setMarking(null);
    }
  };

  const handleDownloadReport = async ({ format, startDate, endDate, scope }) => {
    setShowDownloadModal(false);
    setDownloadLoading(true);
    setError("");
    try {
      const resp = await projectsAPI.downloadInvoicingReport({
        format,
        startDate,
        endDate,
        status: scope === "ALL" ? "READY_FOR_INVOICING,INVOICED" : scope,
        search: search || undefined,
        customers: filterCustomers.length ? filterCustomers.join(",") : undefined,
        workflows: filterWorkflows.length ? filterWorkflows.join(",") : undefined,
      });
      const url = URL.createObjectURL(
        new Blob([resp.data], {
          type:
            format === "xlsx"
              ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              : "application/pdf",
        }),
      );
      const a = document.createElement("a");
      const label = startDate === endDate ? startDate : `${startDate}_${endDate}`;
      a.href = url;
      a.download = `invoicing-report-${label}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download invoicing report");
    } finally {
      setDownloadLoading(false);
    }
  };

  const customerOptions = allCustomerOptions;
  const workflowOptions = allWorkflowOptions;

  const loadMore = useCallback(() => {
    if (isFetchingRef.current || items.length >= totalItems) return;
    fetchQueue(currentParamsRef.current, pageRef.current + 1);
  }, [fetchQueue, items.length, totalItems]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollBodyRef.current;
    if (!sentinel || !container) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { root: container, rootMargin: "120px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div className="projects-page">
      <div className="projects-top-bar">
        <div className="page-header">
          <h1 style={{ margin: 0 }}>Invoicing Queue</h1>
          <div className="project-page-actions">
            <button
              className="btn-secondary"
              onClick={() => setShowDownloadModal(true)}
              disabled={downloadLoading}
            >
              {downloadLoading ? "Generating..." : "Download Report"}
            </button>
          </div>
        </div>

        <div className="project-filters">
          <input
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <details className="multi-filter-dropdown">
            <summary>{getFilterLabel(filterCustomers, customerOptions, "All Customers")}</summary>
            <div className="multi-filter-menu">
              {customerOptions.map((name) => (
                <label key={name} className="multi-filter-item">
                  <input
                    type="checkbox"
                    checked={filterCustomers.includes(name)}
                    onChange={() => toggleMultiSelect(setFilterCustomers, name)}
                  />
                  {name}
                </label>
              ))}
              {!customerOptions.length && (
                <span className="small-muted" style={{ padding: "4px 8px" }}>No customers</span>
              )}
            </div>
          </details>
          <details className="multi-filter-dropdown">
            <summary>{getFilterLabel(filterWorkflows, workflowOptions, "All Workflows")}</summary>
            <div className="multi-filter-menu">
              {workflowOptions.map((name) => (
                <label key={name} className="multi-filter-item">
                  <input
                    type="checkbox"
                    checked={filterWorkflows.includes(name)}
                    onChange={() => toggleMultiSelect(setFilterWorkflows, name)}
                  />
                  {name}
                </label>
              ))}
              {!workflowOptions.length && (
                <span className="small-muted" style={{ padding: "4px 8px" }}>No workflows</span>
              )}
            </div>
          </details>
          <button
            className="btn-secondary btn-sm"
            onClick={() => { setSearch(""); setFilterCustomers([]); setFilterWorkflows([]); }}
          >
            Clear Filters
          </button>
        </div>

        <div className="projects-tabs">
          <button
            className={`projects-tab-btn ${activeTab === "READY_FOR_INVOICING" ? "active" : ""}`}
            onClick={() => setActiveTab("READY_FOR_INVOICING")}
          >
            Ready to Invoice ({readyCount})
          </button>
          <button
            className={`projects-tab-btn ${activeTab === "INVOICED" ? "active" : ""}`}
            onClick={() => setActiveTab("INVOICED")}
          >
            Invoiced ({invoicedCount})
          </button>
        </div>
      </div>

      <div className="projects-scroll-body" ref={scrollBodyRef}>

        {error && (
          <div className="small-muted" style={{ color: "#b91c1c", marginBottom: 8 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="small-muted">Loading invoicing queue...</div>
        ) : items.length === 0 ? (
          <div className="panel">
            <div className="small-muted">
              {search || filterCustomers.length || filterWorkflows.length
                ? "No projects match the current filters."
                : "No projects in this tab."}
            </div>
          </div>
        ) : (
          <div className="projects-list">
            {items.map((item) => {
              const users = item.allocatedUsers || [];
              const projectImages = (
                Array.isArray(item?.imageUrl)
                  ? item.imageUrl
                  : item?.imageUrl
                    ? [item.imageUrl]
                    : []
              ).filter((url) => !url.toLowerCase().endsWith(".stl"));
              const primaryImage = projectImages[0] || "";
              return (
                <article key={item.id} className="project-card flex justify-center">
                  {primaryImage ? (
                    <img
                      src={`${process.env.REACT_APP_API_UPLOAD_URL}${primaryImage}`}
                      alt={item.name}
                    />
                  ) : (
                    <div className="project-img-placeholder">No Image</div>
                  )}

                  <div className="project-content">
                    <div className="project-title-row">
                      <h3 style={{ margin: 0 }}>
                        <Link to={`/projects/${item.id}`}>{item.name}</Link>
                      </h3>
                    </div>
                    <div className="project-meta-chips">
                      <span className="folder-membership-chip">
                        Customer: {item.customer?.name || "-"}
                      </span>
                      <span className="project-meta-chip">
                        Workflow: {item.workflow?.name || "-"}
                      </span>
                      {item.updatedAt && (
                        <span className="project-meta-chip">
                          Completed: {new Date(item.updatedAt).toLocaleDateString()}
                        </span>
                      )}
                      {item.invoicingFlaggedAt && (
                        <span className="project-meta-chip">
                          {timeAgo(item.invoicingFlaggedAt)}
                        </span>
                      )}
                    </div>
                    {users.length > 0 && (
                      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                        {users.slice(0, 6).map((u) => (
                          <span key={u.id} className="detail-chip">
                            {(u.firstName?.[0] || "").toUpperCase()}
                            {(u.lastName?.[0] || "").toUpperCase()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="project-right">
                    <div className={`status-badge status-${item.status}`}>
                      {formatProjectStatus(item.status)}
                    </div>
                    {item.invoicingStatus === "READY_FOR_INVOICING" ? (
                      <>
                        <span className="invoicing-badge">Ready to Invoice</span>
                        {canManage && (
                          <div className="project-actions">
                            {invoicingInputType === "text" && (
                              <input
                                type="text"
                                placeholder="Invoice number"
                                value={invoiceInputs[item.id] || ""}
                                onChange={(e) =>
                                  setInvoiceInputs((prev) => ({ ...prev, [item.id]: e.target.value }))
                                }
                                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 13 }}
                              />
                            )}
                            {invoicingInputType === "checkbox" && (
                              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                                <input
                                  type="checkbox"
                                  checked={!!invoiceInputs[item.id]}
                                  onChange={(e) =>
                                    setInvoiceInputs((prev) => ({ ...prev, [item.id]: e.target.checked }))
                                  }
                                />
                                Invoice sent / confirmed
                              </label>
                            )}
                            {invoicingInputType === "file" && (
                              <input
                                ref={(el) => { fileInputRefs.current[item.id] = el; }}
                                type="file"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                onChange={(e) =>
                                  setInvoiceInputs((prev) => ({ ...prev, [item.id]: e.target.files?.[0] || null }))
                                }
                                style={{ fontSize: 12 }}
                              />
                            )}
                            <button
                              className="btn-primary"
                              onClick={() => handleMarkInvoiced(item.id)}
                              disabled={marking === item.id}
                            >
                              {marking === item.id ? "Marking..." : "Mark as Invoiced"}
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="invoiced-badge">Invoiced</span>
                        <div className="project-actions">
                          <div className="small-muted">
                            Invoiced on{" "}
                            {item.invoicedAt
                              ? new Date(item.invoicedAt).toLocaleDateString()
                              : "-"}
                          </div>
                          {item.invoiceNumber && (
                            <div className="small-muted">
                              Invoice #: {item.invoiceNumber}
                            </div>
                          )}
                          {item.invoiceFilePath && (
                            <a
                              className="btn-secondary"
                              href={projectsAPI.invoiceFileUrl(item.invoiceFilePath)}
                              target="_blank"
                              rel="noreferrer"
                              style={{ fontSize: 12 }}
                            >
                              View Invoice File
                            </a>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
        <div ref={sentinelRef} />
        {isFetchingMore && (
          <div className="small-muted" style={{ textAlign: "center", padding: "12px 0" }}>
            Loading more...
          </div>
        )}
      </div>

      {showDownloadModal && (
        <InvoicingDownloadModal
          defaultScope={activeTab}
          onConfirm={handleDownloadReport}
          onClose={() => setShowDownloadModal(false)}
        />
      )}
    </div>
  );
}

const FORMAT_OPTIONS = [
  { value: "pdf", label: "PDF" },
  { value: "xlsx", label: "Excel" },
];

const SCOPE_OPTIONS = [
  { value: "READY_FOR_INVOICING", label: "Ready to Invoice" },
  { value: "INVOICED", label: "Invoiced" },
  { value: "ALL", label: "Both" },
];

function OptionRow({ checked, onChange, label, name }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        cursor: "pointer",
        fontSize: 13,
        padding: "6px 8px",
        borderRadius: 6,
        background: checked ? "#eff6ff" : "#f9fafb",
        border: "1px solid",
        borderColor: checked ? "#bfdbfe" : "#e5e7eb",
        flex: 1,
      }}
    >
      <input type="radio" name={name} checked={checked} onChange={onChange} />
      <span style={{ fontWeight: 500 }}>{label}</span>
    </label>
  );
}

const sectionLabelStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#374151",
  marginBottom: 10,
};

const dateInputStyle = {
  flex: 1,
  fontSize: 13,
  padding: "4px 8px",
  borderRadius: 6,
  border: "1px solid #e2e8f0",
};

function InvoicingDownloadModal({ defaultScope, onConfirm, onClose }) {
  const [format, setFormat] = useState("pdf");
  const [scope, setScope] = useState(defaultScope || "ALL");
  const [startDate, setStartDate] = useState(todayString);
  const [endDate, setEndDate] = useState(todayString);

  return (
    <div className="um-dialog-overlay" role="dialog" aria-modal="true">
      <div className="um-dialog um-modal-content" style={{ maxWidth: 480 }}>
        <div className="um-dialog-head">
          <h2>Download Report</h2>
          <button
            className="um-close-btn"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div
          className="um-dialog-body"
          style={{ display: "flex", flexDirection: "column", gap: 20 }}
        >
          <div>
            <div style={sectionLabelStyle}>Format</div>
            <div style={{ display: "flex", gap: 8 }}>
              {FORMAT_OPTIONS.map((option) => (
                <OptionRow
                  key={option.value}
                  name="invoicing-report-format"
                  checked={format === option.value}
                  onChange={() => setFormat(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>

          <div>
            <div style={sectionLabelStyle}>Date Range</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 13, color: "#64748b" }}>From</label>
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={dateInputStyle}
              />
              <label style={{ fontSize: 13, color: "#64748b" }}>To</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={dateInputStyle}
              />
            </div>
            <div className="small-muted" style={{ marginTop: 6, fontSize: 12 }}>
              Ready projects are matched by the date they were flagged; invoiced
              projects by the date they were invoiced.
            </div>
          </div>

          <div>
            <div style={sectionLabelStyle}>Include</div>
            <div style={{ display: "flex", gap: 8 }}>
              {SCOPE_OPTIONS.map((option) => (
                <OptionRow
                  key={option.value}
                  name="invoicing-report-scope"
                  checked={scope === option.value}
                  onChange={() => setScope(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="um-dialog-footer">
          <button
            className="um-btn um-btn-primary"
            disabled={!startDate || !endDate}
            onClick={() => onConfirm({ format, startDate, endDate, scope })}
          >
            Download {format === "xlsx" ? "Excel" : "PDF"}
          </button>
          <button
            className="um-btn um-btn-secondary"
            type="button"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
